"use client";

import { Grid2X2, List } from "lucide-react";

import { FormFieldStatus } from "@punchup/ui-common/components/common";
import { VenuePagesRow } from "@punchup/data";
import { cn } from "@punchup/utils/cn";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { updateVenueDefaultShowView } from "../../data/venue_pages";
import { useState } from "react";

interface Props {
  venuePage: VenuePagesRow;
}

export default function VenueShowsDefaultView({ venuePage }: Props) {
  const [defaultShowView, setDefaultShowView] = useState<
    VenuePagesRow["default_show_view"]
  >(venuePage.default_show_view);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supabase = createClientComponentClient();

  const handleUpdateDefaultShowView = async (
    newDefaultShowView: VenuePagesRow["default_show_view"]
  ) => {
    if (newDefaultShowView === defaultShowView) return;
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      await updateVenueDefaultShowView({
        supabase,
        venuePageId: venuePage.id,
        newDefaultShowView,
      });
      setDefaultShowView(newDefaultShowView);
      setSuccess(true);
    } catch (err) {
      console.error("[VenueShowsDefaultView] Error:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleListClick = () => {
    handleUpdateDefaultShowView("list");
  };

  const handleGridClick = () => {
    handleUpdateDefaultShowView("grid");
  };

  return (
    <div className="flex flex-col items-start gap-6">
      <div className="flex flex-row items-center gap-4 md:gap-2 w-full">
        <div className="flex flex-row gap-x-1 bg-[#f7f7f7] rounded-md">
          <button
            onClick={handleGridClick}
            disabled={loading}
            className={cn(
              "p-2 transition-colors rounded-md flex flex-row items-center gap-x-2",
              defaultShowView === "grid"
                ? "stroke-white text-white bg-secondary-blue"
                : "stroke-secondary-blue text-secondary-blue bg-[#f7f7f7] "
            )}
          >
            <Grid2X2 height={24} width={24} className="stroke-inherit" />
            <span className="font-semibold text-inherit">Grid view</span>
          </button>
          <button
            onClick={handleListClick}
            disabled={loading}
            className={cn(
              "p-2 transition-colors rounded-md flex flex-row items-center gap-x-2",
              defaultShowView === "list"
                ? "stroke-white text-white bg-secondary-blue"
                : "stroke-secondary-blue text-secondary-blue bg-[#f7f7f7] "
            )}
          >
            <List height={24} width={24} className="stroke-inherit" />
            <span className="font-semibold text-inherit">List view</span>
          </button>
        </div>
      </div>
      <FormFieldStatus
        loading={loading}
        loadingMessage="Updating default view..."
        success={success}
        successMessage="Default view updated!"
        error={Boolean(error)}
        errorMessage={error ?? ""}
      />
    </div>
  );
}
